import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  ArrowRight, ArrowLeft, CaretDown, CheckCircle, CaretRight,
  Briefcase, Buildings, Lightning, Shield, Sparkle,
  Cpu, Gauge, PlugsConnected, SolarPanel, Fan, ThermometerSimple, Lightbulb,
  Cube, Factory, Bank, Storefront, Broadcast, Package, Wrench, Star,
} from "@phosphor-icons/react";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import BecomePartnerDialog from "../widgets/BecomePartnerDialog";
import ConnectDialog from "../widgets/ConnectDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFIT_ICONS = [Lightning, Buildings, Shield, Briefcase, Sparkle];

const CATEGORY_ICON_MAP = {
  lightning: Lightning, buildings: Buildings, shield: Shield, briefcase: Briefcase, sparkle: Sparkle,
  cpu: Cpu, gauge: Gauge, plugs: PlugsConnected, "plugs-connected": PlugsConnected,
  solar: SolarPanel, "solar-panel": SolarPanel, fan: Fan, thermometer: ThermometerSimple,
  lightbulb: Lightbulb, cube: Cube, factory: Factory, bank: Bank,
  storefront: Storefront, broadcast: Broadcast, package: Package, wrench: Wrench, star: Star,
};

function CategoryBadge({ icon }) {
  const key = String(icon || "").trim().toLowerCase();
  const Icon = CATEGORY_ICON_MAP[key] || Sparkle;
  return (
    <span data-testid="pd-category-icon" className="inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0" style={{ background: "rgba(27,111,224,0.10)", color: "#1B6FE0" }}>
      <Icon size={20} weight="duotone" />
    </span>
  );
}

function FaqRow({ idx, q, a, open, onToggle }) {
  return (
    <li className={`transition-colors ${open ? "bg-slate-50" : ""}`}>
      <button type="button" onClick={onToggle}
        className="w-full text-start flex items-start gap-6 px-6 md:px-8 py-6 hover:bg-slate-50/60 transition"
        data-testid={`pd-faq-toggle-${idx}`}>
        <span className="text-[12px] tabular-nums tracking-wider font-semibold mt-1.5 shrink-0" style={{ color: "#6B7A8D" }}>
          [{String(idx + 1).padStart(2, "0")}]
        </span>
        <span className="flex-1 font-heading font-semibold text-[17px] md:text-[19px] leading-snug tracking-tight"
          style={{ color: "#0F2A6E", letterSpacing: "-0.015em" }}>
          {q}
        </span>
        <CaretDown size={16} weight="bold" className={`mt-1.5 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} style={{ color: "#1B6FE0" }} />
      </button>
      <div className={`grid transition-all duration-400 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="px-6 md:px-8 pb-6 pl-[3.5rem] md:pl-[5rem] text-[14.5px] md:text-[14.5px] text-[#1C2B3A] leading-[1.85] font-light whitespace-pre-line">
            {a}
          </div>
        </div>
      </div>
    </li>
  );
}

function Empty({ message }) {
  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-24 min-h-[60vh] text-center">
      <h1 className="font-heading font-bold text-3xl md:text-4xl" style={{ color: "#0F2A6E" }}>Brand not found</h1>
      <p className="mt-4 text-[#6B7A8D]">{message}</p>
      <Link to="/" className="inline-flex items-center gap-2 mt-8 font-medium" style={{ color: "#1B6FE0" }}>
        <ArrowLeft size={14} weight="bold" /> Back to home
      </Link>
    </div>
  );
}

function CmsImg({ imageId, alt }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!imageId) return;
    let c = false;
    fetch(`${API}/images/${imageId}`).then((r) => r.json()).then((j) => !c && setSrc(j.data_url)).catch(() => {});
    return () => { c = true; };
  }, [imageId]);
  if (!src) return null;
  return <img src={src} alt={alt} className="w-full h-full object-cover" />;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/brands/${slug}`)
      .then((r) => { if (!cancelled) setBrand(r.data); })
      .catch(() => { if (!cancelled) setError("This brand isn't published yet."); });
    return () => { cancelled = true; };
  }, [slug]);

  if (error) return <><Header /><Empty message={error} /><Footer /></>;
  if (!brand) return <><Header /><div className="min-h-[60vh] flex items-center justify-center text-[#6B7A8D]">Loading…</div><Footer /></>;

  const d = brand.detail || {};
  const hasDetail = !!d.hero_h1;

  // Fallback for brands without filled detail content
  if (!hasDetail) {
    return (
      <>
        <Header />
        <main className="bg-white">
          <section className="relative overflow-hidden py-24 md:py-32 text-white"
            style={{ background: "linear-gradient(135deg, #0B1F4D 0%, #0F2A6E 60%, #1B6FE0 100%)" }}>
            <div className="absolute inset-0 grid-bg opacity-12" />
            <div className="relative max-w-5xl mx-auto px-4 lg:px-8">
              <Link to="/#products" className="inline-flex items-center gap-2 text-[12.5px] text-blue-200/80 hover:text-white mb-6">
                <ArrowLeft size={13} weight="bold" /> All brands
              </Link>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-[60px] tracking-tight leading-[1.05]" style={{ letterSpacing: "-0.025em" }}>
                {brand.name}
              </h1>
              {brand.tagline && <p className="mt-5 text-[16px] md:text-[18px] text-slate-200/90 font-light max-w-2xl">{brand.tagline}</p>}
              {brand.description && <p className="mt-3 text-[15px] text-slate-200/80 font-light max-w-3xl">{brand.description}</p>}
              <div className="mt-8 flex flex-wrap gap-3">
                {brand.external_url && (
                  <a href={brand.external_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-white text-[#0F2A6E] font-medium hover:bg-blue-50">
                    Visit official site <ArrowRight size={14} weight="bold" />
                  </a>
                )}
                <Link to="/#faq" className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-white/40 text-white font-medium hover:bg-white/10">
                  Contact us
                </Link>
              </div>
              <p className="mt-12 text-[13px] text-blue-200/70">A full product page for {brand.name} is being prepared. Use the Contact form for product enquiries today.</p>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="bg-white">
        {/* HERO */}
        <section className="relative overflow-hidden text-white py-16 sm:py-24 md:py-32"
          style={{ background: "linear-gradient(135deg, #0B1F4D 0%, #0F2A6E 55%, #1B6FE0 100%)" }}>
          <div className="absolute inset-0 grid-bg opacity-12" />
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "rgba(27,111,224,0.35)" }} />
          <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-8 md:gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-5">
                  {brand.logo_id ? (
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg overflow-hidden bg-white p-1.5"><CmsImg imageId={brand.logo_id} alt={brand.name} /></div>
                  ) : (
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center font-heading font-bold text-white text-[18px]"
                      style={{ background: "rgba(255,255,255,0.15)" }}>{brand.name?.[0] || "B"}</div>
                  )}
                  <span className="text-[11.5px] uppercase tracking-[0.2em] text-blue-200/80 font-semibold">{brand.name}</span>
                </div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl lg:text-[60px] tracking-tight leading-[1.05]"
                  style={{ letterSpacing: "-0.025em" }} data-testid="pd-hero-h1">
                  {d.hero_h1}
                </h1>
                {d.hero_desc && (
                  <p className="mt-5 sm:mt-6 text-[14px] sm:text-[14.5px] md:text-[17px] text-slate-200/90 leading-relaxed font-light max-w-2xl whitespace-pre-line">
                    {d.hero_desc}
                  </p>
                )}
                <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
                  {d.cta_primary && (
                    <Button onClick={() => document.getElementById("pd-final-cta")?.scrollIntoView({ behavior: "smooth" })}
                      data-testid="pd-cta-primary"
                      className="h-11 sm:h-12 px-5 sm:px-6 text-white hover:brightness-110 font-medium" style={{ background: "#1B6FE0" }}>
                      {d.cta_primary} <ArrowRight size={15} weight="bold" />
                    </Button>
                  )}
                  {d.cta_secondary && (
                    <Link to="/#faq" data-testid="pd-cta-secondary"
                      className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-md border border-white/40 text-white hover:bg-white/10 font-medium text-[14px] sm:text-[14.5px]">
                      {d.cta_secondary}
                    </Link>
                  )}
                </div>
              </div>
              {/* Visual */}
              <div className="lg:col-span-5">
                <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden bg-white/5 border border-white/15 relative">
                  {brand.image_id ? <CmsImg imageId={brand.image_id} alt={brand.name} /> : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/40 font-heading text-[40px] sm:text-[60px] font-bold tracking-tight">{brand.name}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        {(d.overview_heading || d.overview_body) && (
          <section className="py-14 sm:py-20 md:py-24 bg-white">
            <div className="max-w-5xl mx-auto px-4 lg:px-8">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[48px] tracking-tight leading-[1.1] anim anim-up"
                style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
                {d.overview_heading}
              </h2>
              <div className="mt-5 sm:mt-6 text-[14px] sm:text-[14.5px] md:text-[17px] text-[#1C2B3A] leading-[1.85] font-light whitespace-pre-line max-w-3xl">
                {d.overview_body}
              </div>
            </div>
          </section>
        )}

        {/* PRODUCT CATEGORIES */}
        {(d.categories || []).length > 0 && (
          <section className="py-14 sm:py-20 md:py-24" style={{ background: "#F5F7FA" }}>
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[48px] tracking-tight leading-[1.1] mb-8 sm:mb-12 anim anim-up"
                style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
                {d.categories_heading || "Product range"}
              </h2>
              <div className="grid md:grid-cols-2 gap-4 sm:gap-5 stagger anim">
                {d.categories.map((c, i) => (
                  <div key={i} data-testid={`pd-category-${i}`}
                    className="p-6 sm:p-7 md:p-8 rounded-2xl bg-white border border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                    <div className="flex items-center gap-3">
                      <CategoryBadge icon={c.icon} />
                      <h3 className="font-heading font-semibold text-[18px] sm:text-[20px] md:text-[22px] tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.015em" }}>{c.title}</h3>
                    </div>
                    <p className="mt-3 text-[14px] md:text-[14.5px] text-[#1C2B3A] leading-relaxed font-light">{c.desc}</p>
                    {(c.best_for || []).length > 0 && (
                      <>
                        <div className="mt-5 text-[10.5px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#6B7A8D" }}>Best for</div>
                        <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                          {c.best_for.map((b, j) => (
                            <li key={j} className="flex items-start gap-1.5 text-[13px] text-[#1C2B3A]">
                              <CaretRight size={11} weight="bold" className="mt-1 shrink-0" style={{ color: "#1B6FE0" }} />
                              {b}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* KEY BENEFITS */}
        {(d.benefits || []).length > 0 && (
          <section className="py-14 sm:py-20 md:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[48px] tracking-tight leading-[1.1] mb-8 sm:mb-12 anim anim-up"
                style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
                {d.benefits_heading || `Why choose ${brand.name}?`}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 stagger anim">
                {d.benefits.map((b, i) => {
                  const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
                  return (
                    <div key={i} data-testid={`pd-benefit-${i}`}
                      className="p-6 sm:p-7 rounded-2xl border border-slate-200 hover:border-[#1B6FE0]/40 hover:shadow-md transition-all duration-300">
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-5"
                        style={{ background: "rgba(27,111,224,0.10)", color: "#1B6FE0" }}>
                        <Icon size={18} weight="duotone" />
                      </div>
                      <h3 className="font-heading font-semibold text-[17px] sm:text-[18px] tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.01em" }}>{b.title}</h3>
                      <p className="mt-2 text-[13.5px] text-[#6B7A8D] leading-relaxed">{b.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* APPLICATIONS */}
        {(d.applications || []).length > 0 && (
          <section className="py-14 sm:py-20 md:py-24" style={{ background: "#0B1F4D" }}>
            <div className="max-w-5xl mx-auto px-4 lg:px-8 text-white">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1] anim anim-up"
                style={{ letterSpacing: "-0.025em" }}>
                {d.applications_heading || `Where ${brand.name} products are used`}
              </h2>
              {d.applications_intro && <p className="mt-4 text-[14px] sm:text-[14.5px] text-slate-200/85 font-light max-w-2xl">{d.applications_intro}</p>}
              <ul className="mt-8 sm:mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger anim">
                {d.applications.map((a, i) => (
                  <li key={i} data-testid={`pd-app-${i}`}
                    className="p-4 rounded-xl bg-white/[0.04] border border-white/10 hover:border-[#1B6FE0]/50 hover:bg-white/[0.07] transition flex items-start gap-2.5">
                    <CheckCircle size={16} weight="duotone" className="mt-0.5 shrink-0" style={{ color: "#1B6FE0" }} />
                    <span className="text-[14px] text-slate-100/95">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* OPSYS SUPPORT */}
        {(d.support_items || []).length > 0 && (
          <section className="py-14 sm:py-20 md:py-24 bg-white">
            <div className="max-w-5xl mx-auto px-4 lg:px-8">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[48px] tracking-tight leading-[1.1] anim anim-up"
                style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
                {d.support_heading || `How OpSys Digital supports ${brand.name} projects`}
              </h2>
              {d.support_intro && <p className="mt-5 text-[14px] sm:text-[14.5px] text-[#1C2B3A] leading-relaxed font-light max-w-3xl">{d.support_intro}</p>}
              <ul className="mt-6 sm:mt-8 space-y-3 max-w-3xl">
                {d.support_items.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] sm:text-[14.5px] text-[#1C2B3A]" data-testid={`pd-support-${i}`}>
                    <CheckCircle size={18} weight="duotone" className="mt-0.5 shrink-0" style={{ color: "#1B6FE0" }} />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* FAQs */}
        {(d.faqs || []).length > 0 && (
          <section className="py-14 sm:py-20 md:py-24" style={{ background: "#F5F7FA" }}>
            <div className="max-w-5xl mx-auto px-4 lg:px-8">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[48px] tracking-tight leading-[1.1] mb-8 sm:mb-10 anim anim-up"
                style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
                {d.faqs_heading || `${brand.name} FAQs`}
              </h2>
              <ul className="rounded-3xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
                {d.faqs.map((f, i) => (
                  <FaqRow key={i} idx={i} q={f.q} a={f.a}
                    open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section id="pd-final-cta" className="py-14 sm:py-20 md:py-28 relative overflow-hidden text-white"
          style={{ background: "linear-gradient(135deg, #0F2A6E 0%, #1B6FE0 100%)" }}>
          <div className="absolute inset-0 grid-bg opacity-12" />
          <div className="relative max-w-4xl mx-auto px-4 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1] anim anim-up"
              style={{ letterSpacing: "-0.025em" }}>
              {d.final_cta_heading || `Need ${brand.name} solutions for your project?`}
            </h2>
            {d.final_cta_body && (
              <p className="mt-5 text-[14px] sm:text-[14.5px] md:text-[17px] text-slate-200/90 font-light leading-relaxed max-w-2xl mx-auto">{d.final_cta_body}</p>
            )}
            <div className="mt-8 sm:mt-9 flex flex-wrap justify-center gap-3">
              <button type="button" onClick={() => setConnectOpen(true)} data-testid="pd-final-cta-primary"
                className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-7 rounded-md bg-white text-[#0F2A6E] font-semibold hover:bg-blue-50">
                {d.final_cta_text || "Send an inquiry"} <ArrowRight size={15} weight="bold" />
              </button>
              <button onClick={() => setPartnerOpen(true)} data-testid="pd-final-cta-partner"
                className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-7 rounded-md border border-white/40 text-white font-medium hover:bg-white/10">
                Become a partner
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BecomePartnerDialog open={partnerOpen} onOpenChange={setPartnerOpen} />
      <ConnectDialog open={connectOpen} onOpenChange={setConnectOpen} />
    </>
  );
}
